import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/vue-query';
import { promiseTimeout } from '@vueuse/core';
import { request } from '@/api/request';
import { ApiError } from '@/api/errors';
import useFeatureFlags, { FeatureFlag } from '@/composables/feature-flags';
import useHasPermissions from '@/modules/current-user/permissions';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type MutationOptions,
  type QueryOptions,
} from '@/modules/query/utils';

import { SUPPLIER_ASSESSMENT_REVIEW_QUERY } from './queryKeys';

export type AssessmentReviewGenerationStatus = 'enqueued' | 'in_progress' | 'complete' | 'error';

const FINDING_TYPE = {
  MissingResponse: 'missing_response',
  WeakEvidence: 'weak_evidence',
  PolicyGap: 'policy_gap',
  InsufficientInformation: 'insufficient_information',
  NotCompliant: 'not_compliant',
} as const;

type FindingType = (typeof FINDING_TYPE)[keyof typeof FINDING_TYPE];

// Defines the possible recommended actions for a finding.
// Order is the default order in which the actions are displayed in the UI.
const RECOMMENDED_ACTION = {
  Discussion: 'discussion',
  Remediation: 'remediation',
  Risk: 'risk',
} as const;

export type RecommendedAction = (typeof RECOMMENDED_ACTION)[keyof typeof RECOMMENDED_ACTION];

export const RECOMMENDED_ACTIONS: RecommendedAction[] = Object.values(RECOMMENDED_ACTION);

// UI-only filter values: the recommended actions plus a `noAction` sentinel for
// rows without a finding. `noAction` is never sent by the API, so it must not
// widen `RecommendedAction`.
export const AI_ACTION_FILTER = { NoAction: 'noAction', ...RECOMMENDED_ACTION } as const;
export type AIActionFilterValue = (typeof AI_ACTION_FILTER)[keyof typeof AI_ACTION_FILTER];

type DismissalSource = 'manual' | 'auto_risk' | 'auto_remediation' | 'auto_discussion';

// A single finding. It carries its own `controlID` / `productID` so it can be
// matched to an assessment row directly — the response has no joined key.
export type AIReviewFinding = {
  id: string;
  controlID: string;
  productID: string | null;
  type: FindingType;
  rationale: string;
  evidence: string;
  actionMessage: string;
  action: RecommendedAction | null;
  // Dismissal state. Dismissed findings are excluded from counts and row
  // aggregates, but kept in the drawer so a dismissal can be undone.
  // `dismissalSource` is null while the finding is not dismissed;
  // `dismissalChangedBy` / `dismissalChangedAt` record the latest toggle in
  // either direction.
  dismissed: boolean;
  dismissalSource: DismissalSource | null;
  dismissalChangedBy: string | null;
  dismissalChangedAt: string | null;
};

export type AIReviewDomainResult =
  | { status: 'failed' }
  | {
      status: 'completed';
      summary?: string;
      findings?: AIReviewFinding[];
    };

// The full result tree, keyed by domain UUID. A domain only appears when it has
// output, and a completed domain only lists controls that have findings.
type AssessmentReviewResult = Record<string, AIReviewDomainResult>;

export function getAssessmentReviewFindingsCount(
  result: AssessmentReviewResult | undefined,
  domainID?: string,
): number {
  const getDomainCount = (domain: AIReviewDomainResult | undefined) =>
    domain?.status === 'completed'
      ? (domain.findings?.filter((finding) => !finding.dismissed).length ?? 0)
      : 0;
  if (domainID) return getDomainCount(result?.[domainID]);
  return Object.values(result ?? {}).reduce((total, domain) => total + getDomainCount(domain), 0);
}

// Index every completed domain's findings by `controlID`, so a row can pull
// its findings in O(1) and then narrow to its product. Empty when there is no
// result (e.g. the flag is off and nothing was fetched). Dismissed findings are
// excluded by default (row icons, filters and totals ignore them); the drawer
// opts in with `includeDismissed` so a dismissal can be shown and undone.
export function buildAssessmentReviewFindingsByControlID(
  result: AssessmentReviewResult | undefined,
  includeDismissed = false,
): Map<string, AIReviewFinding[]> {
  const byControlID = new Map<string, AIReviewFinding[]>();
  for (const domain of Object.values(result ?? {})) {
    if (domain.status !== 'completed') {
      continue;
    }
    for (const finding of domain.findings ?? []) {
      if (!includeDismissed && finding.dismissed) {
        continue;
      }
      const existing = byControlID.get(finding.controlID);
      if (existing) {
        existing.push(finding);
      } else {
        byControlID.set(finding.controlID, [finding]);
      }
    }
  }
  return byControlID;
}

export function findingsForControlAndProduct(
  findingsByControlID: Map<string, AIReviewFinding[]>,
  controlID: string,
  productID: string | null | undefined,
): AIReviewFinding[] {
  return (findingsByControlID.get(controlID) ?? []).filter(
    (finding) => finding.productID === (productID ?? null),
  );
}

export type AssessmentReviewResponse = {
  // The review UUID. Absent while a generation is only optimistically enqueued.
  id?: string;
  status: AssessmentReviewGenerationStatus;
  result?: AssessmentReviewResult;
};

const POLL_INTERVAL_MS = 5_000;

const REFRESH_INITIAL_DELAY_MS = 250;
const REFRESH_BACKOFF_FACTOR = 2;
const REFRESH_MAX_DURATION_MS = 10_000;

// Some actions can cause AI findings to eventually be dismissed in the backend.
// Refetch with a backoff until the review changes, then give up.
export async function refreshAssessmentReviewEventually(
  queryClient: QueryClient,
  supplierID: string,
): Promise<void> {
  const queryKey = [SUPPLIER_ASSESSMENT_REVIEW_QUERY, supplierID];
  const giveUpAt = Date.now() + REFRESH_MAX_DURATION_MS;

  const refetch = async (delay: number): Promise<void> => {
    const remaining = giveUpAt - Date.now();
    if (remaining <= 0) return;
    await promiseTimeout(Math.min(delay, remaining));

    const before = queryClient.getQueryData(queryKey);
    await queryClient.invalidateQueries({ queryKey });
    // Structural sharing keeps the previous object when a refetch is deeply
    // equal, so a new reference means the review actually changed.
    if (queryClient.getQueryData(queryKey) !== before) return;

    await refetch(delay * REFRESH_BACKOFF_FACTOR);
  };

  await refetch(REFRESH_INITIAL_DELAY_MS);
}

// Statuses for which generation is still in flight and GET should keep polling.
const PENDING_STATUSES: AssessmentReviewGenerationStatus[] = ['enqueued', 'in_progress'];

export function getIsPendingReviewStatus(
  status: AssessmentReviewGenerationStatus | undefined,
): boolean {
  return status !== undefined && PENDING_STATUSES.includes(status);
}

// GET the AI assessment review for a connection, polling while it is still
// generating.
export function useAssessmentReview(supplierID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const { ff } = useFeatureFlags();
  const { hasAnyOfPermissions, Permission } = useHasPermissions();

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_ASSESSMENT_REVIEW_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<AssessmentReviewResponse>(
          'GET',
          `/suppliers/${queryKey[1]}/assessment-review`,
        );
        return res.data;
      },
      enabled: () =>
        ff(FeatureFlag.AIAssessmentReview) &&
        hasAnyOfPermissions(Permission.ClientsSuppliersView) &&
        hasAnyOfPermissions(Permission.ClientsRisksView) &&
        hasAnyOfPermissions(Permission.ClientsSuppliersNotesView),
      refetchInterval({ state }) {
        return getIsPendingReviewStatus(state.data?.status) ? POLL_INTERVAL_MS : false;
      },
    }),
  );

  const status = computed(() => query.data.value?.status);
  const result = computed(() => query.data.value?.result);

  // A 204 (no review yet) resolves to an empty body, so a settled query with no
  // status is the "not started" state
  const isNotStarted = computed(() => query.isSuccess.value && status.value === undefined);

  const isGenerationFailedForDomain = (domainID: string | undefined) =>
    domainID && result.value?.[domainID]?.status === 'failed';

  return { query, status, result, isNotStarted, isGenerationFailedForDomain };
}

// POST to request generation. Optimistically marks the review `enqueued` and
// merges the server response on success.
export function useGenerateAssessmentReview(
  options?: MutationOptions<AssessmentReviewResponse, { supplierID: string }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ supplierID }) {
        try {
          const res = await request<AssessmentReviewResponse>(
            'POST',
            `/suppliers/${supplierID}/assessment-review/generate`,
          );
          return res.data;
        } catch (err) {
          // If the kill switch is enabled we can only return the latest review.
          // Silently handle the error, and invalidate the query to ensure
          // we have the latest review generation.
          if (err instanceof ApiError && err.response?.status === 503) {
            await queryClient.invalidateQueries({
              queryKey: [SUPPLIER_ASSESSMENT_REVIEW_QUERY, supplierID],
            });
            const latest = queryClient.getQueryData<AssessmentReviewResponse>([
              SUPPLIER_ASSESSMENT_REVIEW_QUERY,
              supplierID,
            ]);
            if (latest) {
              return latest;
            }
          }
          throw err;
        }
      },
      onMutate({ supplierID }) {
        queryClient.setQueryData<AssessmentReviewResponse>(
          [SUPPLIER_ASSESSMENT_REVIEW_QUERY, supplierID],
          { status: 'enqueued' },
        );
        return null;
      },
      onSuccess(data, { supplierID }) {
        queryClient.setQueryData<AssessmentReviewResponse>(
          [SUPPLIER_ASSESSMENT_REVIEW_QUERY, supplierID],
          data,
        );
      },
    }),
  );
}

export type SetAssessmentReviewFindingDismissedVariables = {
  supplierID: string;
  findingID: string;
  // `true` dismisses the finding (POST .../dismiss), `false` un-dismisses it
  // (POST .../undismiss).
  dismissed: boolean;
};

// POST to dismiss/un-dismiss a single finding, then invalidate the review query
export function useSetAssessmentReviewFindingDismissed(
  options?: MutationOptions<AIReviewFinding, SetAssessmentReviewFindingDismissedVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ supplierID, findingID, dismissed }) {
        const res = await request<AIReviewFinding>(
          'POST',
          `/suppliers/${supplierID}/assessment-review/findings/${findingID}/${
            dismissed ? 'dismiss' : 'undismiss'
          }`,
        );
        return res.data;
      },
      async onSuccess(_data, { supplierID }) {
        await queryClient.invalidateQueries({
          queryKey: [SUPPLIER_ASSESSMENT_REVIEW_QUERY, supplierID],
        });
      },
    }),
  );
}

export type AIFindingRating = 'positive' | 'negative';

export type SubmitAIFindingFeedbackPayload = {
  supplierID: string;
  findingID: string;
  rating: AIFindingRating;
  comment?: string;
};

export function useSubmitAIFindingFeedback(
  options?: MutationOptions<void, SubmitAIFindingFeedbackPayload>,
) {
  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ supplierID, findingID, rating, comment }) {
        await request(
          'POST',
          `/suppliers/${supplierID}/assessment-review/findings/${findingID}/feedback`,
          { rating, comment },
        );
      },
    }),
  );
}
